import { ContentType } from "../../fetchApiDistributor/contentTypes";
import { IImportFileViewDTO, IImportFilesBatchRequestDTO, IImportFilesFilterDTO, ILoadAndGetImportedInvoiceByFileNameDTO, ILocalFileDTO } from "../../coreapi-dto/dirs/globalImportInvoices";
import { IEntitySimpleDTO } from "../../coreapi-dto/@types/common";
import { IInvoiceImportedDTO } from "../../coreapi-dto/documents/importedInvoice";
import { CoreApiResponse, IRequestEventHandler } from "../common";
import BaseCoreApiRequest from "../baseCoreApiRequest";

class GlobalImportInvoicesRequest extends BaseCoreApiRequest {
    constructor(baseUrl: string, token: string, eventHandler: IRequestEventHandler) {
        super(baseUrl);
        this._token = token;
        this._eventHandler = eventHandler;
    }

    private _token: string;
    private _eventHandler: IRequestEventHandler;

    private getDefaultHeaders() {
        return this.concatHeaders([this.getBearerHeader(this._token)], [ContentType.ApplicationJson]);
    }

    private mapFilterToBody(filter: IImportFilesFilterDTO) {
        return {
            pageNumber: filter.pageNumber ?? 1,
            numberPerPage: filter.numberPerPage ?? 100,
            fieldOrderBy: filter.fieldOrderBy ?? "supplierDocDate",
            orderDesc: filter.orderDesc ?? true,
            idContractorGlobal: filter.idContractorGlobal ?? null,
            idSupplierGlobal: filter.idSupplierGlobal ?? null,
            idStoreGlobal: filter.idStoreGlobal ?? null,
            fileName: filter.fileName ?? "",
            supplierDocNum: filter.supplierDocNum ?? "",
            search: filter.search ?? "",
            supplierDocDateFrom: filter.supplierDocDateFrom ? filter.supplierDocDateFrom.toISO() : null,
            supplierDocDateTo: filter.supplierDocDateTo ? filter.supplierDocDateTo.toISO() : null,
            status: filter.status ?? null
        };
    }

    getImportFilesView(filter: IImportFilesFilterDTO, callback?: (e: CoreApiResponse<IViewWrapper<IImportFileViewDTO>>) => void) {
        this.post<IViewWrapper<IImportFileViewDTO>>(
            `${this._baseUrl}/ImportFiles/Invoices/AutoLoading/GetImportFilesView`,
            this.mapFilterToBody(filter),
            this.getDefaultHeaders(),
            (e) => {
                if (this._eventHandler && e.respType === "isFailed") {
                    this._eventHandler.onGetResponse(e);
                }
                callback?.(e);
            }
        );
    }

    loadAndGetByFileName(dto: ILoadAndGetImportedInvoiceByFileNameDTO, callback?: (e: CoreApiResponse<IInvoiceImportedDTO | null>) => void) {
        this.post2<IInvoiceImportedDTO | null>(
            `${this._baseUrl}/ImportFiles/Invoices/AutoLoading/LoadAndGetByFileName`,
            dto,
            this.getDefaultHeaders(),
            (e) => {
                if (this._eventHandler && e.respType === "isFailed") {
                    this._eventHandler.onGetResponse(e);
                }
                callback?.(e);
            }
        );
    }

    importByFiles(request: IImportFilesBatchRequestDTO, callback?: (e: CoreApiResponse<IBatchImportResultDTO>) => void) {
        this.post<IBatchImportResultDTO>(
            `${this._baseUrl}/ImportFiles/Invoices/Batch/ImportByFiles`,
            request,
            this.getDefaultHeaders(),
            (e) => {
                if (this._eventHandler && e.respType === "isFailed") {
                    this._eventHandler.onGetResponse(e);
                }
                callback?.(e);
            }
        );
    }

    importAndCreate(request: IImportFilesBatchRequestDTO, callback?: (e: CoreApiResponse<IBatchImportResultDTO>) => void, signal?: AbortSignal) {
        this.post<IBatchImportResultDTO>(
            `${this._baseUrl}/ImportFiles/Invoices/Batch/ImportAndCreate`,
            request,
            this.getDefaultHeaders(),
            (e) => {
                if (this._eventHandler && e.respType === "isFailed") {
                    this._eventHandler.onGetResponse(e);
                }
                callback?.(e);
            },
            signal
        );
    }

    deleteFilesLocal(files: ILocalFileDTO[], callback?: (e: CoreApiResponse<boolean>) => void) {
        this.post<boolean>(
            `${this._baseUrl}/ImportFiles/Invoices/AutoLoading/DeleteMultipleFilesLocal`,
            files,
            this.getDefaultHeaders(),
            (e) => {
                if (this._eventHandler && e.respType === "isFailed") {
                    this._eventHandler.onGetResponse(e);
                }
                callback?.(e);
            }
        );
    }

    preview(file: ILocalFileDTO, callback?: (e: CoreApiResponse<IImportInvoiceResultDTO>) => void) {
        this.post<IImportInvoiceResultDTO>(
            `${this._baseUrl}/ImportFiles/Invoices/AutoLoading/Preview`,
            file,
            this.getDefaultHeaders(),
            (e) => {
                if (this._eventHandler && e.respType === "isFailed") {
                    this._eventHandler.onGetResponse(e);
                }
                callback?.(e);
            }
        );
    }

    previewBatch(files: ILocalFileDTO[], callback?: (e: CoreApiResponse<IBatchImportResultDTO>) => void) {
        this.post<IBatchImportResultDTO>(
            `${this._baseUrl}/ImportFiles/Invoices/AutoLoading/PreviewBatch`,
            files,
            this.getDefaultHeaders(),
            (e) => {
                if (this._eventHandler && e.respType === "isFailed") {
                    this._eventHandler.onGetResponse(e);
                }
                callback?.(e);
            }
        );
    }
}

export interface IBatchImportResultDTO {
    isSucceed: boolean;
    totalFiles: number;
    successCount: number;
    failedCount: number;
    bunchCount: number;
    results: IImportInvoiceResultDTO[];
    generalErrors: string[];
}

export interface IImportInvoicePreviewItemDTO {
    goodsName?: string;
    producer?: string;
    country?: string;
    quantity?: number;
    supplierCostInfo?: {
        price?: number;
        sum?: number;
        sumIncVat?: number;
        vat?: number;
    };
    retailCostInfo?: {
        priceIncVat?: number;
    };
    goodsCode?: string;
    barcode?: string;
    goods?: IEntitySimpleDTO;
}

export interface IImportInvoiceResultDTO {
    isSucceed: boolean;
    fileName?: string;
    idImportedInvoiceGlobal?: string;
    createdInvoiceGlobal?: string;
    isPendingMatching?: boolean;
    errorsList: string[];
    typeBunches?: ITypeBunch[];
    supplierDocNum?: string;
    supplierDocDate?: string;
    itemCount?: number;
    sumIncVat?: number;
    sumWithoutVat?: number;
    vatSum?: number;
    items?: IImportInvoicePreviewItemDTO[];
}

export interface ITypeBunch {
    type: string;
    idInvoiceItem: string;
}

export default GlobalImportInvoicesRequest;
